import React, { useEffect } from "react";
import useSWR from "swr";
import { useState } from "react";
import DOMPurify from "dompurify";
import { SECURITY_CONFIG, RateLimiter } from "../config/security";

function PopupSection({ popup, title = null, img = null }) {
  const [formErrors, setFormErrors] = useState({});
  const rateLimiter = new RateLimiter(SECURITY_CONFIG.RATE_LIMIT_DURATION);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
    company_name: "",
    designation: "",
    location: "",
    gdprConsent: false,
    form_title: "",
  });

  // Input sanitization function
  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+\d{10,15}$/;
    const specialCharRegex = /[^a-zA-Z0-9\s]/;

    // Helper function to check special chars
    const hasSpecialChar = (value) => specialCharRegex.test(value);

    // Name
    if (!formData.name.trim()) {
      errors.name = "Name is required.";
    } else if (hasSpecialChar(formData.name)) {
      errors.name = "Special characters are not allowed.";
    }

    // Phone
    if (!formData.phone.trim()) {
      errors.phone = "Phone is required.";
    } else if (!phoneRegex.test(formData.phone)) {
      errors.phone =
        "Please start with '+', include country code and add 10 to 15 numbers.";
    }

    // Email
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email)) {
      errors.email = "Invalid email format.";
    }

    // Location
    if (!formData.location || !formData.location.trim()) {
      errors.location = "Location is required.";
    } else if (hasSpecialChar(formData.location)) {
      errors.location = "Special characters are not allowed.";
    }

    // Message
    if (!formData.message.trim()) {
      errors.message = "Message is required.";
    } else if (hasSpecialChar(formData.message)) {
      errors.message = "Special characters are not allowed.";
    }

    if (img !== "facilities") {
      // Service
      if (!formData.service) {
        errors.service = "Please select a service.";
      } else if (hasSpecialChar(formData.service)) {
        errors.service = "Special characters are not allowed.";
      }

      // Company Name
      if (!formData.company_name || !formData.company_name.trim()) {
        errors.company_name = "Company name is required.";
      } else if (hasSpecialChar(formData.company_name)) {
        errors.company_name = "Special characters are not allowed.";
      }

      // Designation
      if (!formData.designation || !formData.designation.trim()) {
        errors.designation = "Designation is required.";
      } else if (hasSpecialChar(formData.designation)) {
        errors.designation = "Special characters are not allowed.";
      }

      // GDPR Consent
      if (!formData.gdprConsent) {
        errors.gdprConsent = "You must agree to the GDPR policy.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // email subject
  useEffect(() => {
    const subject_title =
      img === "facilities"
        ? "Sky47 Request a Facility Tour Form Submission"
        : "Sky47 Connect with Our Experts Form Submission";

    setFormData((prev) => ({
      ...prev,
      form_title: subject_title,
    }));
  }, [img]);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setFormData({ ...formData, [e.target.name]: sanitizedValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    // Rate limiting check
    if (!rateLimiter.canProceed()) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime() / 1000);
      setStatus(
        `Please wait ${remainingTime} seconds before submitting again.`
      );
      return;
    }

    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      const res = await fetch(
        `${SECURITY_CONFIG.API_BASE_URL}${SECURITY_CONFIG.CONTACT_ENDPOINT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Origin: window.location.origin,
          },
          credentials: "omit", // Don't send cookies for cross-origin requests
          body: JSON.stringify(formData),
        }
      );

      const result = await res.json();
      if (result.success) {
        setStatus("Message sent successfully!");
        setFormData({
          name: "",
          phone: "",
          email: "",
          service: "",
          message: "",
          company_name: "",
          designation: "",
          location: "",
          form_title: "",
        });
        setFormErrors({});
      } else {
        setStatus("Failed to send message.");
      }
    } catch (err) {
      setStatus("Error sending message.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="Modal_main">
        <div
          className={`popup_main ${
            img === "facilities"
              ? "book_a_tour_popup"
              : "talkto_our_experts_popup"
          }`}
        >
          <button onClick={() => popup(false)} className="close_btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M2.66683 23.6667L0.333496 21.3333L9.66683 12L0.333496 2.66667L2.66683 0.333332L12.0002 9.66667L21.3335 0.333332L23.6668 2.66667L14.3335 12L23.6668 21.3333L21.3335 23.6667L12.0002 14.3333L2.66683 23.6667Z"
                fill="#28B25D"
              />
            </svg>
          </button>
          <div className="left-section">
            <img
              src={
                img === "facilities"
                  ? "https://47.activ8.digital/wp-content/uploads/2025/08/Book-Tour-Image.png"
                  : "https://47.activ8.digital/wp-content/uploads/2025/08/Talk-to-our-Experts.png"
              }
              alt="Customer Support"
              className="hero-image"
            />
          </div>

          <div className="right-section">
            <h2 className="section-title">
              {title ? title : "Connect with Our Experts"}{" "}
            </h2>
            {img == "facilities" ? (
              ""
            ) : (
              <p className="section-para">
                {" "}
                Get Custom Cloud, AI or Colocation Advice
              </p>
            )}

            <form
              id="contactForm"
              className="contact-form"
              onSubmit={handleSubmit}
            >
              <div className="form-row">
                <input
                  type="hidden"
                  name="form_title"
                  onChange={handleChange}
                  value={formData.form_title}
                />
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-input"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {formErrors.name && (
                    <p className="form-error">{formErrors.name}</p>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Ph. Number</label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    className="form-input"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    title="Please enter a valid phone number (10-15 digits, may start with +)"
                  />
                  {formErrors.phone && (
                    <p className="form-error">{formErrors.phone}</p>
                  )}
                </div>
                {img == "facilities" ? (
                  ""
                ) : (
                  <div className="form-group">
                    <label className="form-label">Company Name</label>
                    <input
                      type="text"
                      className="form-input"
                      name="company_name"
                      placeholder="Enter Company Name....."
                      value={formData.companyName}
                      onChange={handleChange}
                    />
                    {formErrors.company_name && (
                      <p className="form-error">{formErrors.company_name}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="form-row form-row-2">
                {img == "facilities" ? (
                  ""
                ) : (
                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input
                      type="text"
                      className="form-input"
                      name="designation"
                      placeholder="Enter Designation......."
                      value={formData.designation}
                      onChange={handleChange}
                    />
                    {formErrors.designation && (
                      <p className="form-error">{formErrors.designation}</p>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-input"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {formErrors.email && (
                    <p className="form-error">{formErrors.email}</p>
                  )}
                </div>
                {img == "facilities" ? (
                  ""
                ) : (
                  <div className="form-group">
                    <label className="form-label">Select Service</label>
                    <select
                      name="service"
                      className="form-select"
                      value={formData.service}
                      onChange={handleChange}
                    >
                      <option value="">Choose Services</option>
                      <option value="colocation">Colocation</option>
                      <option value="cloud">Cloud</option>
                      <option value="managed-security">Managed Security</option>
                      <option value="GPUaaS">GPUaaS</option>
                    </select>
                    {formErrors.service && (
                      <p className="form-error">{formErrors.service}</p>
                    )}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Select Location</label>
                  <select
                    name="location"
                    className="form-select"
                    value={formData.location}
                    onChange={handleChange}
                  >
                    <option value="">Choose Location</option>
                    <option value="islamabad">Islamabad</option>
                    <option value="lahore">Lahore</option>
                    <option value="karachi">Karachi</option>
                  </select>
                  {formErrors.location && (
                    <p className="form-error">{formErrors.location}</p>
                  )}
                </div>
              </div>

              <div className="form-group full-width">
                <label className="form-label">Message</label>
                <textarea
                  className="form-textarea"
                  name="message"
                  placeholder="Briefly describe your need or question…"
                  value={formData.message}
                  onChange={handleChange}
                ></textarea>
                {formErrors.message && (
                  <p className="form-error">{formErrors.message}</p>
                )}
              </div>

              {img == "facilities" ? (
                ""
              ) : (
                <div className="privacy_checkbox_main">
                  <div className="form-row checkbox_main">
                    <input
                      type="checkbox"
                      name="gdprConsent"
                      checked={formData.gdprConsent}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          gdprConsent: e.target.checked,
                        })
                      }
                    />
                    <p>
                      I consent to the collection and processing of my personal
                      data in accordance with{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Sky47’s Privacy Policy.
                      </a>
                    </p>
                  </div>
                  {formErrors.gdprConsent && (
                    <p className="form-error">{formErrors.gdprConsent}</p>
                  )}
                </div>
              )}

              <button
                type="submit"
                className="submit-button submit-btn"
                disabled={loading}
              >
                {loading
                  ? "Sending..."
                  : img == "facilities"
                  ? "Submit"
                  : "Request Expert Callback"}
                <img
                  src="https://47.activ8.digital/wp-content/uploads/2025/08/uil_arrow-up.png"
                  alt="Arrow"
                />
              </button>
              {status && <p style={{ marginTop: "10px" }}>{status}</p>}
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
export default PopupSection;
